import type { AccessState } from '@bbbk/schema';

// G17 — one SHAPE per access state, so the line reads without the colour.
//
// A coloured dot said "something" and never which thing: six states cannot live
// on four tints, and two of them are ambers. Read left to right these are a life
// cycle — an empty ring, a sent plane, a clock that ran out, an arc still
// turning, a tick, a ban — and the shape carries the meaning while the colour
// only reinforces it. That is what keeps the row legible in mono, at low
// contrast, and to anyone who cannot separate the two ambers (§9).
//
// Lucide, verbatim, as `Icon.tsx` is: circle-dashed, send-horizontal, timer-off,
// loader-circle, circle-check-big, ban.
//
// ONE DEVIATION FROM THE PROTOTYPE, and it is deliberate. The prototype draws
// `expired` with an Icons8 PNG pulled from img.icons8.com through a CSS mask,
// because Icons8's SVG is their paid path. A desk screen must not fetch an icon
// from a third party at render time: it is a request per open that leaks when
// Patricia looks at a household, and it draws nothing at all when the kennel's
// broadband is down — silently, since a failed mask leaves an empty box. Lucide's
// `timer-off` is the same metaphor, ships in the bundle, and takes currentColor.
const PATHS: Record<AccessState, string[]> = {
  none: [
    'M10.1 2.182a10 10 0 0 1 3.8 0',
    'M13.9 21.818a10 10 0 0 1-3.8 0',
    'M17.609 3.721a10 10 0 0 1 2.69 2.7',
    'M2.182 13.9a10 10 0 0 1 0-3.8',
    'M20.279 17.609a10 10 0 0 1-2.7 2.69',
    'M21.818 10.1a10 10 0 0 1 0 3.8',
    'M3.721 6.391a10 10 0 0 1 2.7-2.69',
    'M6.391 20.279a10 10 0 0 1-2.69-2.7',
  ],
  invited: [
    'M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z',
    'M6 12h16',
  ],
  expired: ['M10 2h4', 'M4.6 11a8 8 0 0 0 1.7 8.7 8 8 0 0 0 8.7 1.7', 'M7.4 7.4a8 8 0 0 1 10.3 1 8 8 0 0 1 .9 10.2', 'm2 2 20 20', 'M12 12v-2'],
  part: ['M21 12a9 9 0 1 1-6.219-8.56'],
  live: ['M21.801 10A10 10 0 1 1 17 3.335', 'm9 11 3 3L22 4'],
  revoked: ['M4.929 4.929 19.07 19.071'],
};

export function AccessGlyph({ state, size = 14 }: { state: AccessState; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {state === 'revoked' && <circle cx="12" cy="12" r="10" />}
      {PATHS[state].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
